import React, { useEffect, useState } from "react";
import { Box, Tab, Tabs } from "@mui/material";
import {
  ArrayField,
  ArrayInput,
  BooleanField,
  BooleanInput,
  Datagrid,
  FunctionField,
  SimpleFormIterator,
  TextInput,
  useLocaleState,
  useSourceContext,
} from "react-admin";
import { useFormContext, useFormState } from "react-hook-form";

const normalizeLocale = (locale) => {
  const language = (locale || "ar").toLowerCase();
  if (language.startsWith("en")) return "en";
  if (language.startsWith("ur")) return "ur";
  return "ar";
};

const getQuestionSettingLabels = (locale) => {
  const language = normalizeLocale(locale);

  const labels = {
    ar: {
      questionsList: "قائمة الأسئلة",
      question: "السؤال",
      questionAr: "السؤال بالعربية",
      questionEn: "السؤال بالإنجليزية",
      questionUr: "السؤال بالأردية",
      initialExam: "مخصص للامتحان الأولي",
      retryExam: "مخصص لإعادة الامتحان",
      answers: "الإجابات",
      answerText: "نص الإجابة",
      answerAr: "الإجابة بالعربية",
      answerEn: "الإجابة بالإنجليزية",
      answerUr: "الإجابة بالأردية",
      correctAnswer: "إجابة صحيحة",
      requiredField: "هذا الحقل مطلوب",
      minAnswers: "يجب إضافة إجابة واحدة على الأقل",
      minCorrectAnswer: "يجب وضع علامة على إجابة صحيحة واحدة على الأقل",
      minIncorrectAnswer: "يجب وجود إجابة غير صحيحة واحدة على الأقل",
      examAssignment: "يجب اختيار واحد على الأقل من الامتحان الأولي أو إعادة الامتحان لكل سؤال",
      initialExamColumn: "امتحان أولي",
      retryExamColumn: "إعادة امتحان",
      answerColumn: "الإجابة",
      correctColumn: "صح",
    },
    en: {
      questionsList: "Questions list",
      question: "Question",
      questionAr: "Question in Arabic",
      questionEn: "Question in English",
      questionUr: "Question in Urdu",
      initialExam: "For initial exam",
      retryExam: "For retry exam",
      answers: "Answers",
      answerText: "Answer text",
      answerAr: "Answer in Arabic",
      answerEn: "Answer in English",
      answerUr: "Answer in Urdu",
      correctAnswer: "Correct answer",
      requiredField: "This field is required",
      minAnswers: "At least one answer is required",
      minCorrectAnswer: "At least one correct answer must be marked",
      minIncorrectAnswer: "At least one incorrect answer is required",
      examAssignment: "Each question must be assigned to at least one exam type",
      initialExamColumn: "Initial exam",
      retryExamColumn: "Retry exam",
      answerColumn: "Answer",
      correctColumn: "Correct",
    },
    ur: {
      questionsList: "سوالات کی فہرست",
      question: "سوال",
      questionAr: "عربی میں سوال",
      questionEn: "انگریزی میں سوال",
      questionUr: "اردو میں سوال",
      initialExam: "ابتدائی امتحان کے لیے",
      retryExam: "دوبارہ امتحان کے لیے",
      answers: "جوابات",
      answerText: "جواب کا متن",
      answerAr: "عربی میں جواب",
      answerEn: "انگریزی میں جواب",
      answerUr: "اردو میں جواب",
      correctAnswer: "درست جواب",
      requiredField: "یہ فیلڈ ضروری ہے",
      minAnswers: "کم از کم ایک جواب ضروری ہے",
      minCorrectAnswer: "کم از کم ایک درست جواب کا نشان لگانا ضروری ہے",
      minIncorrectAnswer: "کم از کم ایک غلط جواب ضروری ہے",
      examAssignment: "ہر سوال کو کم از کم ایک امتحان کی قسم میں شامل کرنا ضروری ہے",
      initialExamColumn: "ابتدائی امتحان",
      retryExamColumn: "دوبارہ امتحان",
      answerColumn: "جواب",
      correctColumn: "درست",
    },
  };

  return labels[language] || labels.ar;
};

const MultilingualTextInput = ({ sourcePrefix, labels, validate }) => {
  const [activeLanguage, setActiveLanguage] = useState("ar");
  const sourceContext = useSourceContext();
  const { control, getFieldState } = useFormContext();
  const languageFields = [
    { id: "ar", label: "العربية", direction: "rtl", inputLabel: labels.ar },
    { id: "en", label: "English", direction: "ltr", inputLabel: labels.en },
    { id: "ur", label: "اردو", direction: "rtl", inputLabel: labels.ur },
  ];
  const fieldSources = languageFields.map((language) =>
    sourceContext.getSource(`${sourcePrefix}_${language.id}`)
  );
  const formState = useFormState({ control, name: fieldSources });

  const hasLanguageError = (languageIndex) =>
    getFieldState(fieldSources[languageIndex], formState).invalid;

  useEffect(() => {
    if (formState.submitCount === 0) return;

    const firstInvalidLanguage = languageFields.findIndex((_, index) =>
      hasLanguageError(index)
    );

    if (firstInvalidLanguage >= 0) {
      setActiveLanguage(languageFields[firstInvalidLanguage].id);
    }
  }, [formState.submitCount]);

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Tabs
        value={activeLanguage}
        onChange={(_, language) => setActiveLanguage(language)}
        variant="fullWidth"
        sx={{
          minHeight: 34,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "action.hover",
        }}
      >
        {languageFields.map((language, index) => (
          <Tab
            key={language.id}
            value={language.id}
            label={`${language.label} *`}
            sx={{
              minHeight: 34,
              py: 0,
              fontSize: "0.78rem",
              color: hasLanguageError(index) ? "error.main" : undefined,
              "&.Mui-selected": {
                color: hasLanguageError(index) ? "error.main" : undefined,
              },
            }}
          />
        ))}
      </Tabs>

      {languageFields.map((language) => (
        <Box
          key={language.id}
          sx={{
            display: activeLanguage === language.id ? "block" : "none",
            px: 1.25,
            pt: 1,
          }}
          dir={language.direction}
        >
          <TextInput
            source={`${sourcePrefix}_${language.id}`}
            label={language.inputLabel}
            validate={validate}
            multiline
            minRows={1}
            maxRows={3}
            size="small"
            margin="dense"
            fullWidth
          />
        </Box>
      ))}
    </Box>
  );
};

export const QuestionSettingArrayInput = ({ source, label, className }) => {
  const [locale] = useLocaleState();
  const labels = getQuestionSettingLabels(locale);

  const requiredFieldValidator = (value) => {
    if (value === undefined || value === null || (typeof value === "string" && !value.trim())) {
      return labels.requiredField;
    }

    return undefined;
  };

  const minAnswersValidator = (value) => {
    if (!Array.isArray(value) || value.length === 0) {
      return labels.minAnswers;
    }

    return undefined;
  };

  const minCorrectAnswerValidator = (value) => {
    if (!Array.isArray(value) || value.length === 0) {
      return labels.minAnswers;
    }

    const hasCorrectAnswer = value.some((item) => item?.is_correct === true);
    if (!hasCorrectAnswer) {
      return labels.minCorrectAnswer;
    }

    return undefined;
  };

  const minIncorrectAnswerValidator = (value) => {
    if (!Array.isArray(value) || value.length === 0) {
      return labels.minAnswers;
    }

    const hasIncorrectAnswer = value.some((item) => item?.is_correct !== true);
    if (!hasIncorrectAnswer) {
      return labels.minIncorrectAnswer;
    }

    return undefined;
  };

  const examAssignmentValidator = (value) => {
    if (!Array.isArray(value)) {
      return undefined;
    }

    const hasInvalidQuestion = value.some((item) => {
      const isInitialExam = item?.is_initial_exam === true;
      const isRetryExam = item?.is_retry_exam === true;
      return !isInitialExam && !isRetryExam;
    });

    if (hasInvalidQuestion) {
      return labels.examAssignment;
    }

    return undefined;
  };

  return (
    <ArrayInput
      source={source}
      label={label || labels.questionsList}
      className={className}
      validate={[examAssignmentValidator]}
    >
      <SimpleFormIterator
        disableReordering={false}
        fullWidth
        getItemLabel={(index) => `${labels.question} ${index + 1}`}
        sx={{
          "& .RaSimpleFormIterator-line": { py: 1 },
          "& .RaSimpleFormIterator-form": { gap: 1 },
        }}
      >
        <MultilingualTextInput
          sourcePrefix="question_text"
          labels={{ ar: labels.questionAr, en: labels.questionEn, ur: labels.questionUr }}
          validate={[requiredFieldValidator]}
        />
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            "& .MuiFormControl-root": { my: 0 },
          }}
        >
          <BooleanInput
            source="is_initial_exam"
            label={labels.initialExam}
            defaultValue={false}
          />
          <BooleanInput
            source="is_retry_exam"
            label={labels.retryExam}
            defaultValue={false}
          />
        </Box>

        <ArrayInput
          source="answers"
          label={labels.answers}
          className={className}
          validate={[minAnswersValidator, minCorrectAnswerValidator, minIncorrectAnswerValidator]}
        >
          <SimpleFormIterator
            disableReordering={false}
            fullWidth
            getItemLabel={(index) => `${labels.answerColumn} ${index + 1}`}
            sx={{
              "& .RaSimpleFormIterator-line": { py: 0.75 },
              "& .RaSimpleFormIterator-form": { gap: 0.75 },
            }}
          >
            <MultilingualTextInput
              sourcePrefix="answer_text"
              labels={{ ar: labels.answerAr, en: labels.answerEn, ur: labels.answerUr }}
              validate={[requiredFieldValidator]}
            />
            <BooleanInput
              source="is_correct"
              label={labels.correctAnswer}
              defaultValue={false}
            />
          </SimpleFormIterator>
        </ArrayInput>
      </SimpleFormIterator>
    </ArrayInput>
  );
};

export const QuestionSettingArrayField = ({ source, label, className }) => {
  const [locale] = useLocaleState();
  const labels = getQuestionSettingLabels(locale);

  const getLocalizedText = (record, baseField) => {
    const values = [
      record?.[`${baseField}_ar`],
      record?.[`${baseField}_en`],
      record?.[`${baseField}_ur`],
    ].filter((value) => value !== undefined && value !== null && String(value).trim() !== "");

    return values.join(" | ");
  };

  return (
    <ArrayField source={source} label={label || labels.questionsList} className={className}>
      <Datagrid bulkActionButtons={false}>
        <FunctionField
          label={labels.question}
          render={(record) => getLocalizedText(record, "question_text")}
        />
        <BooleanField source="is_initial_exam" label={labels.initialExamColumn} />
        <BooleanField source="is_retry_exam" label={labels.retryExamColumn} />
        <ArrayField source="answers" label={labels.answers}>
          <Datagrid bulkActionButtons={false}>
            <FunctionField
              label={labels.answerColumn}
              render={(record) => getLocalizedText(record, "answer_text")}
            />
            <BooleanField source="is_correct" label={labels.correctColumn} />
          </Datagrid>
        </ArrayField>
      </Datagrid>
    </ArrayField>
  );
};

export default QuestionSettingArrayInput;
